"use client";

import { useMemo, useState } from "react";
import { Search, X, Plus, Minus } from "lucide-react";
import type { HeaderData, LanguageOption } from "../../types";
import { RuntimeImage, RuntimeLink, useLocale } from "../../runtime/RendererRuntime";
import { GuardedLink, useDevPlaceholder } from "../../runtime/DevPlaceholder";
import { t } from "../../i18n/strings";
import { resolveConfiguredHref } from "../../utils/links";
import { LanguageSelector } from "./LanguageSelector";

/** Props for the SiteHeaderMobile component. */
export interface SiteHeaderMobileProps {
  data: HeaderData;
  logoSrc: string;
  logoAlt: string;
  homeHref: string;
  language: string;
  languageOptions: LanguageOption[];
  currentLanguage: LanguageOption | undefined;
  onLanguageChange: (code: string) => void;
  onClose: () => void;
}

/* Brand palette — every surface either uses one of these tokens or a transparent variant.
   Centralised here so the mobile menu reads as one cohesive object. */
const APA_BLUE = "#00B862";
const APA_BLUE_BG_SOFT = "#EEF1F0"; // very light blue tint for accent rows
const APA_BLUE_BG_HOVER = "#EEF1F0"; // hover/active row tint
const APA_BLUE_BORDER = "rgba(0, 92, 169, 0.15)"; // subtle dividers

/** Mobile menu overlay with accordion navigation, language picker, search link and quick links.
 * Redesign: white background, APA blue accents, soft-blue accent surfaces. The dark navy
 * variant lived alongside the prod design system; this aligns with the rest of the public site. */
export function SiteHeaderMobile({
  data,
  logoSrc,
  logoAlt,
  homeHref,
  language,
  languageOptions,
  currentLanguage,
  onLanguageChange,
  onClose,
}: SiteHeaderMobileProps) {
  const [mobileNavOpenItems, setMobileNavOpenItems] = useState<Record<string, boolean>>({});
  const { previewMode, notify } = useDevPlaceholder();
  const locale = useLocale();

  // Production drops unconfigured links; preview keeps them for the dev modal. Mirrors SiteHeader:
  // a dropdown section with no resolvable children is hidden entirely (even with its own href).
  const navItems = useMemo(() => {
    if (previewMode) return data.nav;
    return data.nav
      .filter((item) => {
        const rawChildren = item.children ?? [];
        if (rawChildren.length > 0) return rawChildren.some((child) => resolveConfiguredHref(child.href));
        return Boolean(resolveConfiguredHref(item.href));
      })
      .map((item) => ({ ...item, children: item.children?.filter((child) => resolveConfiguredHref(child.href)) }));
  }, [data.nav, previewMode]);
  const quickLinks = previewMode ? data.quickLinks : data.quickLinks.filter((link) => resolveConfiguredHref(link.href));

  return (
    <div className="fixed inset-0 z-40 md:hidden" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-[#121212]/40" onClick={onClose} />
      <div
        id="mobile-menu"
        className="relative ml-auto flex h-full w-full flex-col bg-white"
        style={{ color: APA_BLUE }}
      >
        {/* Header bar — same height/visual as the desktop header so the transition feels seamless. */}
        <div
          className="flex items-center justify-between bg-white px-3 py-2 min-h-[70px]"
          style={{ borderBottom: `1px solid ${APA_BLUE_BORDER}` }}
        >
          <RuntimeLink href={homeHref} className="flex items-center gap-2 pr-2 min-w-0" aria-label={logoAlt} onClick={onClose}>
            <RuntimeImage
              src={logoSrc}
              alt={logoAlt}
              className="h-9 w-auto max-w-full object-contain"
              style={{ maxWidth: "min(70vw, 320px)" }}
            />
            <span className="sr-only">APA</span>
          </RuntimeLink>
          <button
            type="button"
            onClick={onClose}
            /* Soft tint + subtle border matches the search/lang pills below — same visual
             * weight as the rest of the app's secondary buttons, instead of the heavier
             * solid-blue outline that read as a primary action. */
            className="inline-flex items-center justify-center gap-2 rounded-md px-3 py-2 text-[15px] xl:text-[17px] font-semibold leading-none my-1 transition-colors"
            style={{
              border: `1px solid ${APA_BLUE_BORDER}`,
              color: APA_BLUE,
              backgroundColor: APA_BLUE_BG_SOFT,
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = APA_BLUE_BG_HOVER)}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = APA_BLUE_BG_SOFT)}
          >
            <X className="h-4 w-4" />
            <span className="leading-none">Menu</span>
            <span className="sr-only">{t(locale, "close")}</span>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {/* Search + language strip — soft blue tint sets it apart from the nav rows below. */}
          <div
            className="flex items-center justify-between gap-3 px-4 py-3 text-sm xl:text-base"
            style={{ backgroundColor: APA_BLUE_BG_SOFT, borderBottom: `1px solid ${APA_BLUE_BORDER}` }}
          >
            <RuntimeLink
              href="/search"
              onClick={onClose}
              className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-[15px] xl:text-[17px] font-semibold transition-colors"
              style={{ backgroundColor: "white", color: APA_BLUE, border: `1px solid ${APA_BLUE_BORDER}` }}
              aria-label={t(locale, "search")}
            >
              <Search className="h-4 w-4" />
              <span className="leading-none">{t(locale, "searchPageTitle")}</span>
            </RuntimeLink>
            <LanguageSelector
              variant="mobile"
              language={language}
              languageOptions={languageOptions}
              currentLanguage={currentLanguage}
              onLanguageChange={onLanguageChange}
            />
          </div>

          {/* Primary nav — each row is a clean white surface with a single divider; expanded
              children sit on the soft-blue tint so they read as a group. */}
          <nav className="text-sm xl:text-base">
            {navItems.map((item, idx) => {
              const sectionKey = item.label || `section-${idx}`;
              const hasChildren = (item.children ?? []).length > 0;
              const isOpen = mobileNavOpenItems[sectionKey] ?? false;
              const itemHref = resolveConfiguredHref(item.href);

              const toggleSection = () => {
                if (!hasChildren) return;
                setMobileNavOpenItems((prev) => ({
                  ...prev,
                  [sectionKey]: !prev[sectionKey],
                }));
              };

              return (
                <div key={sectionKey} style={{ borderBottom: `1px solid ${APA_BLUE_BORDER}` }}>
                  <div
                    className="flex items-center justify-between bg-white px-4 py-3.5 text-[16px] xl:text-[18px] font-semibold transition-colors"
                    style={{ color: APA_BLUE }}
                  >
                    {itemHref ? (
                      <RuntimeLink
                        href={itemHref}
                        onClick={(e) => {
                          e.stopPropagation();
                          onClose();
                        }}
                        className="flex-1 hover:underline"
                      >
                        {item.label}
                      </RuntimeLink>
                    ) : hasChildren ? (
                      <span className="flex-1">{item.label}</span>
                    ) : (
                      // Empty leaf — preview only (filtered in prod): open the dev modal instead of nothing.
                      <button
                        type="button"
                        data-dev-placeholder="true"
                        className="flex-1 text-left hover:underline cursor-pointer"
                        style={{ color: APA_BLUE }}
                        onClick={() => notify(item.label)}
                      >
                        {item.label}
                      </button>
                    )}
                    {hasChildren && (
                      <button
                        type="button"
                        aria-expanded={isOpen}
                        aria-controls={`mobile-nav-${idx}`}
                        onClick={toggleSection}
                        className="ml-3 grid h-9 w-9 place-items-center rounded-full transition-colors"
                        style={{
                          backgroundColor: isOpen ? APA_BLUE : "white",
                          /* Idle pill needs ≥3:1 vs the white row to meet WCAG 1.4.11 for UI
                             components. Soft tint alone is 1.07:1, so we add a solid blue border. */
                          border: isOpen ? `1px solid ${APA_BLUE}` : `1px solid ${APA_BLUE}`,
                          color: isOpen ? "white" : APA_BLUE,
                        }}
                        aria-label={isOpen ? t(locale, "collapse") : t(locale, "expand")}
                      >
                        {isOpen ? <Minus className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                      </button>
                    )}
                  </div>
                  {hasChildren && isOpen && (
                    <ul
                      id={`mobile-nav-${idx}`}
                      className="px-4 py-3"
                      style={{ backgroundColor: APA_BLUE_BG_SOFT, borderTop: `1px solid ${APA_BLUE_BORDER}` }}
                    >
                      {item.children?.map((c) => {
                        return (
                          <li key={c.label}>
                            <GuardedLink
                              label={c.label}
                              className="block py-2 text-[15px] xl:text-[17px] hover:underline"
                              href={c.href}
                              onClick={onClose}
                              style={{ color: APA_BLUE }}
                            >
                              {c.label}
                            </GuardedLink>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              );
            })}
          </nav>

          {/* Quick links — softer, smaller, with a clear visual break from the main nav. */}
          {quickLinks.length > 0 && (
            <div
              className="px-4 py-4 text-sm xl:text-base"
              style={{ backgroundColor: APA_BLUE_BG_SOFT, borderTop: `1px solid ${APA_BLUE_BORDER}` }}
            >
              <p
                /* Full-strength blue (6.41:1 on the soft tint, AA pass). The previous 60%
                 * opacity gave only 2.79:1 — below WCAG AA for normal text. */
                className="mb-2 text-[11px] font-bold uppercase tracking-[0.12em]"
                style={{ color: APA_BLUE }}
              >
                {data.quickLinksLabel ?? t(locale, "quickLinks")}
              </p>
              <ul className="space-y-1.5">
                {quickLinks.map((link) => (
                  <li key={link.label}>
                    <GuardedLink
                      label={link.label}
                      className="block py-1 text-[15px] xl:text-[17px] font-medium hover:underline"
                      href={link.href}
                      onClick={onClose}
                      style={{ color: APA_BLUE }}
                    >
                      {link.label}
                    </GuardedLink>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
